// Wire shapes. Two protocols live here:
//
//  1. The HTTP API under /api, spoken by the web client and the CLI.
//  2. The "studio" service channel between the page and the Pocket apps on
//     the laptop, carried as JSON lines over the spec svc ops.

import type { Lang } from "./i18n.ts";
import type { AgentId, AgentLink, AppInfo, AppKind, DeviceId, ListingMedia, NativeTarget, PackageInfo, RemixOf, SessionInfo, SessionPhase, StudioSnapshot, Takedown, UserInfo, Want } from "./types.ts";

// ---------------------------------------------------------------- HTTP API

/** GET /api/me: 200 with the user, 401 when signed out. */
export interface MeResponse {
  user: UserInfo;
}

/** GET /api/auth/providers. `github` is false until its OAuth app is configured. */
export interface AuthProvidersResponse {
  /** Present with the accounts system: Google sign-in is configured. */
  google?: boolean;
  /** Present with the accounts system: sign-in by emailed code is on. */
  email?: boolean;
  github: boolean;
  guest: boolean;
}

/** GET /api/studio. */
export type StudioResponse = StudioSnapshot;

/**
 * PUT /api/studio/skin. `skin` is a catalog skin id, or CUSTOM_SKIN with
 * `color` as the `#rrggbb` the account mixed. A member colour and a custom
 * colour answer 402 with kind `membership` for an account that is not a member.
 */
export interface SetSkinRequest {
  device: DeviceId;
  skin: string;
  color?: string;
}

/**
 * PUT /api/studio/handle, for an account that is not a guest (403 for a
 * guest), answers the new snapshot. PUT /api/cli/account takes the same body
 * with the account token and answers CliWhoResponse. A handle is 3 to 24
 * lowercase letters, digits and single inner hyphens; a leading `@` and
 * capitals are taken off what was sent. 400 when it is not one or is
 * reserved (an editor may take a reserved one), 409 when another account has
 * it. The handle the account had is free for anyone at once. Every published
 * game of the account is signed with the new one from this answer on.
 */
export interface SetHandleRequest {
  handle: string;
}

/** PUT /api/studio/room. 402 when the room is not owned. */
export interface SetRoomRequest {
  room: string;
}

/** PUT /api/studio/skin, PUT /api/studio/room and PUT /api/apps/:id answer the new snapshot. */
export type SnapshotResponse = StudioSnapshot;

/**
 * PUT /api/apps/:id. Only the author may change an app. Publishing gives the
 * app of an account (not of a guest) an address when it has none.
 */
export interface UpdateAppRequest {
  /** Allow downloads of the game's installation packages, or stop it. */
  allowNative?: boolean;
  status?: "draft" | "published";
  title?: string;
  /**
   * The label of the game's own host. 400 when it is not a valid label, 403
   * for a guest, 409 when another game holds it or when another account gave
   * it up less than 30 days ago. The label the game had stays held for this
   * account for 30 days: this account takes it again, nobody else can.
   */
  slug?: string;
}

/**
 * DELETE /api/apps/:id, for the app's author, answers the snapshot without
 * the app. DELETE /api/cli/apps/:id takes the account token of the app's
 * owner (a session token does not delete) and answers `{ ok: true }`. Either
 * one unpublishes the app, takes it off the shelf, drops an editor's marks,
 * deletes its sessions, builds, packages, unfinished uploads and site files,
 * and gives up its address, which stays held for the account for 30 days.
 * Nothing of it can be brought back.
 */
export type DeleteAppResponse = SnapshotResponse;

/** What DELETE /api/cli/apps/:id answers. */
export interface CliDeleteResponse {
  ok: true;
}

/**
 * PUT /api/apps/:id/editorial, for an editor (a Studio user id listed in
 * POCKET_EDITOR_IDS); 403 for anyone else. Sets one or both marks and leaves
 * a mark the body does not name as it was. `featured` puts a published
 * project in discovery and answers 404 for a project that is not published.
 * `verified` is recorded for any app id, published or not: a `site` project
 * is verified before it can deploy, which is before it can be published.
 * PUT /api/apps/:id/featured is the same route under its first name.
 */
export interface EditorialRequest {
  featured?: boolean;
  verified?: boolean;
  /**
   * The place an editor gives a featured project in the showcase: a whole
   * number from 0 to 999, lower first; null takes the rank off. Projects
   * with no rank come after the ranked ones, the one featured last first.
   * PUT /api/cli/showcase sets the first games' ranks in one request.
   */
  rank?: number | null;
}

/** The project's marks after the change. */
export interface EditorialResponse {
  ok: true;
  featured: boolean;
  verified: boolean;
  /** Present while the project has a rank. */
  rank?: number;
}

/** One installation package of a showcased game. */
export interface ShowcasePackage {
  target: NativeTarget;
  /** The device's name in plain text: `PS Vita`. */
  name: string;
  filename: string;
  size: number;
}

/** One game of the showcase. Every address is absolute. */
export interface ShowcaseGame {
  id: string;
  slug: string;
  title: string;
  author: string;
  tagline: string;
  /** The game's own address, where its browser version runs. */
  url: string;
  /** The game's page on the Studio's host. */
  page: string;
  /** The first media entry of its listing. */
  lead: ListingMedia;
  /** Whether its address runs the game in a browser: a web build, or a Pocket app's builds. */
  browser: boolean;
  verified: boolean;
  /** The packages an account can download, one per device; empty while its author keeps downloads off. */
  packages: ShowcasePackage[];
  /** Whether a game of one's own can start from a copy of this one. */
  remixable: boolean;
  /** Whether there is something to download: a package, or the game's source. */
  downloads: boolean;
}

/** One earlier version of a game's package for a device. */
export interface PastPackage {
  target: NativeTarget;
  /** The device's name in the answer's language. */
  name: string;
  filename: string;
  size: number;
  version: string;
  uploadedAt: number;
}

/** A game as a row of another game's page: a remix of it, or the game it was remixed from. */
export interface RelatedGame {
  id: string;
  title: string;
  author: string;
  tagline: string;
  /** The game's page, or null for a game that has none (a built-in game). */
  page: string | null;
  /** Its listing's card or its cover, or null. */
  picture: string | null;
  /** The devices it has packages for, by name in the answer's language, when its author lets them be downloaded. */
  devices: string[];
  /** Whether its own address runs it in a browser. */
  browser: boolean;
}

/** A neighbour of a game in the row the play page turns through. */
export interface FeedGame {
  slug: string;
  title: string;
  tagline: string;
  /** The game's own address, where its browser version runs. */
  url: string;
  page: string;
  /** A still of the game: its lead clip's poster or its lead picture. */
  picture: string | null;
}

/**
 * GET /api/games/:slug: what a published game's page, the landing page's
 * shelf and the play page's dock need to offer Play, Download and Remix. It
 * reads no cookie, answers any origin (`access-control-allow-origin: *`) and
 * a cache may keep it for a minute. `?lang=` names the language of the
 * device names and of the listing's line.
 *
 * `packages` are the current ones, one per device, and `past` the earlier
 * versions kept, newest first. A current package is any account's to
 * download; a past one is a member's (GET /api/apps/:id/native/:device).
 * Both are empty while the author keeps downloads off.
 */
export interface GameFacts {
  id: string;
  slug: string;
  kind: AppKind;
  title: string;
  author: string;
  tagline: string;
  verified: boolean;
  /** The game's own address when it runs the game in a browser; null otherwise. */
  play: string | null;
  page: string;
  /** A still of the game: its listing's lead poster or picture, or its cover. */
  picture: string | null;
  packages: Array<ShowcasePackage & { version: string; uploadedAt: number; made?: true }>;
  past: PastPackage[];
  /** Whether it has packages that Pocket Nexus has not tested. */
  homebrew: boolean;
  /**
   * The game's source. `repository`: a public repository, with its host's
   * name and the address of an archive of its default branch (GitHub,
   * GitLab and Codeberg make one). `archive`: the source the Studio holds of
   * a Pocket app: `url` hands it to a signed-in account and sends anyone else
   * to the game's page (GET /api/get/:slug/source).
   */
  source:
    | { kind: "repository"; url: string; host: string; archive: string | null }
    | { kind: "archive"; url: string; size: number }
    | null;
  remixable: boolean;
  /** The game this one was started from, when it is a remix. */
  remixOf: RelatedGame | null;
  /** The published games started from this one, newest first, at most REMIXES_SHOWN. */
  remixes: RelatedGame[];
  /** How many published games were started from this one. */
  remixCount: number;
  /** The games before and after this one in the showcase's order, which wraps. Null when the showcase has no other game. */
  feed: { previous: FeedGame; next: FeedGame } | null;
}

/** How many remixes a game's page lists. */
export const REMIXES_SHOWN = 24;

/**
 * GET /api/gate: who is asking, for a page that offers a download or a
 * remix: whether an account is signed in (a guest is none), whether it has
 * the membership, the ways to sign in, and the membership's prices for the
 * request's country. It reads the session cookie and is never cached.
 */
export interface GateResponse {
  /** The signed-in account's handle, or null for a visitor and for a guest. */
  account: string | null;
  member: boolean;
  providers: AuthProvidersResponse;
  membership: { open: boolean; price: number; annualPrice: number; currency: "usd" | "jpy" };
}

/** A 401 body for a download or a remix asked without an account. */
export interface SignInRequiredResponse {
  error: "signin_required";
}

/**
 * GET /api/showcase: the games an editor featured that are published, have
 * an address and have a listing. The games an editor placed come first
 * (`rank`, lower first); the others follow, the one featured last first; a
 * tie is settled by title. It reads no cookie and answers everyone the
 * same, and a cache may keep it for a minute. The landing page and the
 * index /games/ are sent with it in them (`#showcase`).
 */
export interface ShowcaseResponse {
  /** The featured games, in the showcase's order: the landing page's shelf, /games and the play page's row. */
  games: ShowcaseGame[];
}

/**
 * PUT /api/cli/showcase, with an editor's account token: the games that
 * come first in the showcase, in order, each named by its label or its app
 * id. They take the ranks 1, 2, 3 and so on, and every other featured game
 * loses its rank. An empty list leaves every game unplaced. A name that is
 * not a game of the showcase, or one named twice, answers 400 and changes
 * nothing. Answers CliShowcaseResponse.
 */
export interface CliShowcaseRequest {
  first: string[];
}

/** GET /api/cli/showcase, with an editor's account token: the showcase's games in its order. */
export interface CliShowcaseResponse {
  games: Array<{
    id: string;
    slug: string;
    title: string;
    /** The place an editor gave it. Absent for a game that follows the placed ones. */
    rank?: number;
    /** When it was featured, in milliseconds. Absent for a game featured before the showcase kept the day. */
    featuredAt?: number;
  }>;
}

/**
 * PUT /api/cli/apps/:id/listing, with the account token of the game's owner
 * or of an editor (for a game that is or was published), or the session's
 * token. `listing` is listing.json as its author wrote it and `files` names
 * the SHA-256 and the size of each file it names. The server checks the
 * listing and every stored file's own bytes (src/core/listing.ts) and
 * replaces the game's listing in one write; the tagline replaces the
 * game's. Answers CliListingResponse.
 *
 * While a file is not stored it answers 409 with CliListingIncomplete, and
 * each hash it names is sent with PUT
 * /api/cli/apps/:id/listing/files/:sha256, the body being the file. The
 * same request is then sent again.
 */
export interface CliListingRequest {
  listing: unknown;
  files: Record<string, { sha256: string; size: number }>;
}

export interface CliListingIncomplete {
  error: string;
  missing: string[];
}

export interface CliListingResponse {
  app: AppInfo;
}

/**
 * DELETE /api/cli/apps/:id/listing: the game's listing and its files are
 * removed. Its author may, and so may an editor for a game that is or was
 * published. `removed` is false when the game had none.
 */
export interface CliListingRemoved {
  ok: true;
  removed: boolean;
}

/**
 * PUT /api/apps/:id/takedown and PUT /api/cli/apps/:id/takedown: an editor
 * takes a game down. `reason` is one sentence or two that its author reads on
 * the game's card. DELETE on either lifts it. Both answer TakedownResponse.
 */
export interface TakedownRequest {
  reason: string;
}

/** The game's takedown after the change: null when none stands. */
export interface TakedownResponse {
  ok: true;
  takedown: Takedown | null;
}

/** A 402 body: the account lacks what was asked for, and `kind` says what would grant it. */
export interface PaymentRequiredResponse {
  error: "payment_required";
  kind: "room" | "membership";
  id: string;
  price: number;
}

/**
 * GET /api/apps/:id/native/:device streams the package the author uploaded
 * for that target, under its own file name. It asks for an account: a
 * visitor and a guest get 401 with SignInRequiredResponse. `?version=` names
 * an earlier version (GameFacts.past), which is a membership feature:
 * an account without it gets 402 with kind `membership`. 403 when the author
 * has not allowed downloads, 404 when there is no package for the target or
 * no such version. A single `Range` is honoured.
 */
export type NativeDownloadDenied = PaymentRequiredResponse;

/**
 * GET /api/apps/:id/native/:device?info=1 makes the same checks and answers
 * this in place of the file, so a page learns the refusal's reason before it
 * sends the browser to `url`.
 */
export interface NativeDownloadInfo {
  /** The download itself: the same route without `info`. */
  url: string;
  filename: string;
  size: number;
  version: string;
}

// The CLI authenticates with `Authorization: Bearer <token>`.

/** POST /api/cli/sessions answers it: a session token, the session and its starter. */
export interface CliLinkResponse {
  token: string;
  user: UserInfo;
  session: SessionInfo;
  /** Starter template the session scaffolds from. */
  starter: string;
  /**
   * For a remix: the game it starts from. With `source` true the project is
   * made from that game's source, which GET /api/cli/sessions/:id/remix
   * answers with the session's token; with false it is a starter, `starter`.
   */
  remix?: RemixOf & { source: boolean };
}

/**
 * POST /api/cli/sessions/:id/events. With a `phase`, the session moves to it
 * and takes the message and the progress. With `agent` and no `phase`, the
 * request says who is working and nothing else: the phase stays, and the
 * session counts as changed now. The command line sends that while an agent
 * runs, every few minutes, so the room can tell an agent at work from a
 * terminal that was closed.
 */
export interface CliEventRequest {
  phase?: SessionPhase;
  message?: string;
  progress?: number | null;
  /** The agent that is working, or null when the one that was has stopped. */
  agent?: AgentId | null;
  /** Where the project is on this machine, written as its user would: `~/games/lander`. The room shows it to the account alone. */
  dir?: string;
}

/**
 * PUT /api/cli/sessions/:id/card, with the session's token: what the shelf
 * says of the session's game. `title` is 1 to 60 characters, `tagline` at
 * most 80, `colors` two `#rrggbb` cover colours. A part left out stays as it
 * was. Answers CliAppResponse.
 */
export interface CliCardRequest {
  title?: string;
  tagline?: string;
  colors?: [string, string];
}

/**
 * PUT /api/cli/sessions/:id/build?device=<id>
 * multipart/form-data with two parts: `js` (the bundle) and `pak` (its assets).
 * The room then loads them from GET /api/builds/:session/:device/:build.js|.pak.
 * 413 with the limit named when the account stores 3 GiB already; so do the
 * routes that start a site deployment and a package upload.
 */
export interface CliBuildResponse {
  session: SessionInfo;
}

/** POST /api/cli/sessions/:id/events answers the session as it now stands. */
export type CliSessionResponse = CliBuildResponse;

/**
 * POST /api/cli/sessions/:id/publish and POST /api/cli/apps/:id/publish.
 * `slug` asks for that address; without it an app that has none gets one
 * made from its title. A guest's app gets no address and `slug` answers 403.
 */
export interface CliPublishRequest {
  slug?: string;
}

/** POST /api/cli/sessions/:id/publish answers the session and the app as published. */
export interface CliSessionPublishResponse extends CliBuildResponse {
  app: AppInfo;
}

// An account link. The room mints a one-time code, the user pastes it to a
// coding agent inside a prompt, and `pocket-studio link <CODE>` exchanges it
// for an account token. That token creates sessions; it opens no session
// route. Each session it creates comes with its own session token.

/**
 * POST /api/links, for the signed-in user. Answers the account's live code
 * while that code has more than half of its 15 minutes left, and a new one
 * otherwise, so opening the popup twice shows one code.
 */
export type CreateLinkResponse = AgentLink;

/**
 * POST /api/cli/account. 404 for an unknown code, 410 for a spent or expired
 * one. A machine that is linked already sends its account token along, and the
 * 410 then says whether the code was minted by that same account.
 */
export interface CliAccountRequest {
  code: string;
}

export interface CliAccountRefused {
  error: string;
  /** True when the caller's account token belongs to the account that minted the code. */
  linked: boolean;
}

export interface CliAccountResponse {
  /** `Authorization: Bearer <token>` for the three routes below. */
  token: string;
  user: UserInfo;
}

/** GET /api/cli/account answers whose token this is. DELETE revokes the token. */
export type CliWhoResponse = MeResponse;

/**
 * POST /api/cli/sessions, with the account token. Omit `app` to start a new
 * game: 403 with the limit named when the account holds 50 projects already.
 * The session is created already linked, and the answer is a CliLinkResponse.
 * GET /api/cli/sessions/:id, with the session's token, answers the SessionInfo.
 */
export interface CliCreateSessionRequest {
  /** Left out with `remix`: the first device the remixed game has a build for. */
  device?: DeviceId;
  app?: string;
  title?: string;
  agent?: AgentId;
  /**
   * Start the new game as a remix of this one: a starter's id, or the id of
   * a published Pocket app that is `remixable`. The title is the game's with
   * " Remix" after it unless `title` says otherwise. 404 when the game cannot
   * be remixed. Not with `app`.
   */
  remix?: string;
}

/**
 * PUT /api/cli/sessions/:id/source, with the session's token: the project's
 * source files as a gzipped ustar archive (src/build/tar.ts), at most
 * SOURCE_LIMITS.bytes. The Studio keeps one per game, the newest, and it is
 * what a remix of the published game starts from. Answers CliAppResponse.
 *
 * GET /api/cli/sessions/:id/remix, with the session's token of a remix:
 * the archive of the game it was started from, as that game is published
 * now. 404 when the session is no remix of a game with a source, or that
 * game is no longer published.
 */
export const SOURCE_LIMITS = {
  /** The gzipped archive. */
  bytes: 8 * 1024 * 1024,
  /** What the command line packs before it gives up: files, and their bytes together. */
  files: 2000,
  unpacked: 24 * 1024 * 1024,
} as const;

// A project by its app id. These routes take the account token of the app's
// owner. They also take a session token of that app when the request names
// the session: `?session=<session id>`.

/**
 * POST /api/cli/apps, with the account token of an account that is not a
 * guest (403 for a guest, and for an account that holds 50 projects
 * already). Registers a game built outside Pocket Studio: an app of kind
 * `site`, with no device session.
 */
export interface CliRegisterRequest {
  title: string;
  tagline?: string;
  /** The two cover colours, `#rrggbb` each. Absent, they follow from the app id, or for a remix from the game it came from. */
  colors?: [string, string];
  /** The public repository of the game's source (parseRepository): once the game is published, others can remix it. */
  repository?: string;
  /**
   * The game this one is a remix of: a published game built outside Pocket
   * Studio with a repository. 404 when it cannot be remixed, 409 for a Pocket
   * app, whose remix is `POST /api/cli/sessions` with `remix`.
   */
  remixOf?: string;
}

/**
 * What POST /api/cli/apps, GET /api/cli/apps/:id and POST
 * /api/cli/apps/:id/publish answer: the app as its owner sees it.
 */
export interface CliAppResponse {
  app: AppInfo;
}

/**
 * PUT /api/cli/apps/:id, with the app's token: what an author sets from a
 * terminal. `allowNative` is the switch "Members can download it" on the
 * game's card. Answers CliAppResponse.
 */
export interface CliAppUpdateRequest {
  allowNative?: boolean;
  /** A site game's public repository, or null to take it off. 409 for a Pocket app. */
  repository?: string | null;
}

/**
 * PUT /api/cli/apps/:id/cover, with the app's token of an account that is
 * not a guest. The body is one PNG picture: at most 2 MiB, 64 to 2048 pixels
 * on each side, not animated. It becomes the game's `shot`, the picture on
 * its case, on its card's rows and on Pocket TV, and replaces the cover the
 * game had. It counts against the account's storage. Answers CliAppResponse.
 *
 * `shot` is then `/api/covers/<app>/<sha256>.png` on the Studio's host: the
 * address names the picture's bytes, so it changes when the picture does,
 * and its answer may be kept for a year.
 */
export type CliCoverResponse = CliAppResponse;

/**
 * DELETE /api/cli/apps/:id/cover: the game's cover picture is removed and
 * `shot` is null again. The game's author may, with an account token or the
 * session's token, and so may an editor with an account token, for a game
 * that is or was published. Answers `{ ok: true, removed }`: `removed` is
 * false when the game had no cover.
 */
export interface CliCoverRemoved {
  ok: true;
  removed: boolean;
}

/** One file of a site: its path inside the site, its size in bytes and the SHA-256 of its bytes in lowercase hex. */
export interface SiteFile {
  path: string;
  size: number;
  sha256: string;
}

/**
 * POST /api/cli/apps/:id/site, for an app of kind `site` that an editor
 * verified; 403 with the reason for one that is not verified, as on the two
 * routes below. Starts a deployment from the list of its files. `entry` is
 * the file `/` serves and must be one of them.
 */
export interface CliSiteRequest {
  files: SiteFile[];
  entry: string;
  /**
   * The devices the entry page says it plays as (`<meta name="pocket-devices">`).
   * 409 when the game has a package for a device that is not among them
   * (`siteRefusal` in `src/core/platforms.ts`). Kept with the deployment, and
   * a package for a device the live site does not name is refused from then on.
   */
  devices?: DeviceId[];
}

/**
 * The deployment's id, and the SHA-256 of every file this app does not hold
 * yet. Each one is sent with PUT
 * /api/cli/apps/:id/site/blobs/:sha256?deployment=<id>, the body being the
 * file; the server checks the size and the hash.
 */
export interface CliSiteResponse {
  deployment: string;
  missing: string[];
}

/**
 * POST /api/cli/apps/:id/site/:deployment/activate makes the deployment the
 * live one and answers CliAppResponse. While files are missing it answers 409
 * with this body, and the files it names are to be sent again.
 */
export interface CliSiteIncomplete {
  error: string;
  missing: string[];
}

/**
 * POST /api/cli/apps/:id/packages/:target/uploads. Starts the upload of one
 * installation package. An upload of the same target that was not completed
 * is dropped.
 *
 * `:id` on the four package routes is an app of the caller's, or the id of a
 * starter. A starter's packages are an editor's to upload, with the account
 * token (403 for anyone else). They count against no account's storage, and
 * members download them like any other package.
 */
export interface CliPackageRequest {
  filename: string;
  /** The whole file, in bytes. */
  size: number;
  version?: string;
}

/**
 * The upload's id and how the file is cut: `parts` parts of `partSize` bytes,
 * the last one holding what is left. Part `n` (from 1) is sent with PUT
 * /api/cli/apps/:id/packages/:target/uploads/:upload/parts/:n.
 */
export interface CliPackageUpload {
  upload: string;
  partSize: number;
  parts: number;
}

/**
 * POST /api/cli/apps/:id/packages/:target/uploads/:upload/complete joins the
 * parts into a package of the target. One with the version of the target's
 * current package, or with no version, replaces it. A later version becomes
 * the current package, and the one before it is kept as a past version. An
 * earlier version joins the past versions (`past`), which members download.
 * DELETE on the upload drops it.
 */
export interface CliPackageResponse {
  package: PackageInfo;
  app: AppInfo;
  /** The upload is an earlier version than the one the game offers. */
  past?: true;
}

/**
 * DELETE /api/cli/starters/:id: what an editor deleted of a built-in game
 * that left the catalog. `packages` are the objects of its packages, by the
 * key each had in the bucket; `uploads` counts the uploads given up.
 */
export interface CliStarterRemoved {
  id: string;
  packages: Array<{ target: NativeTarget; key: string; size: number }>;
  uploads: number;
}

// ------------------------------------------------- studio service channel

/** A theme of the laptop's desktop (Pocket Shell Desktop's theme ids). */
export type DesktopThemeId = "classic" | "xp" | "aqua";

/** page -> laptop app. Sent at boot and after every snapshot change. */
export interface StudioStateLine {
  t: "studio";
  snapshot: StudioSnapshot;
  /** What the room is doing, so apps can reflect it. */
  focus: { kind: "room" | "device" | "app" | "laptop" | "closet" | "tv" | "agent" | "play" | "create"; device?: DeviceId; app?: string };
  /**
   * The desktop's active theme, which the apps draw in. The page leaves it
   * out: the laptop host adds it to every line it delivers and delivers the
   * line again when the theme changes.
   */
  theme?: DesktopThemeId;
  /**
   * What the page asked this app to show: a section of its own, such as a
   * device of the Install Guide. The page leaves it out: the laptop host adds
   * it to the lines of the app `StudioDesktop.launch` named, and forgets it
   * when that app's window closes. `n` counts the requests, so the same
   * section asked for twice is two requests.
   */
  show?: { n: number; section: string };
  /**
   * The language the room shows (src/core/i18n.ts), on every line. The room
   * loads again when the visitor changes it, so a laptop app reads it from
   * the first line it gets and from each line after.
   */
  lang?: Lang;
}

/** laptop app -> page. */
/** The laptop's Studio apps, as the event log names them. */
export const LAPTOP_PROGRAMS = ["games", "settings", "guide"] as const;
export type LaptopProgram = (typeof LAPTOP_PROGRAMS)[number];

export type StudioIntentLine =
  | { t: "play"; app: string; device?: DeviceId }
  | { t: "skin"; device: DeviceId; skin: string }
  | { t: "room"; room: string }
  | { t: "publish"; app: string; on: boolean }
  /** The user asked to become a member, or to manage the membership. */
  | { t: "membership" }
  | { t: "signout" }
  /** One of the laptop's Studio apps started: its window was opened. */
  | { t: "opened"; program: LaptopProgram }
  /** The user chose a desktop theme. The laptop host passes it to the System UI and keeps it for the viewer. */
  | { t: "theme"; theme: DesktopThemeId };
